import {
  Box,
  ChevronDown,
  Loader2,
  PackageOpen,
  PlusCircle,
  Search,
  Weight,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getQuantityDecimalsForProduct } from '@/utils/quantityUtils'
import ImageComponent from '@/components/misc/Images/Image'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InvoiceFullProductData } from '@/interfaces/billing'
import { SalesCategories } from '@/interfaces/ServerInterfaces'
import { cn } from '@/lib/utils'
import { getProductMainCurrency, getProductMainPrice } from '@/utils/priceUtils'

// Skeleton component for product cards during loading
const ProductCardSkeleton = () => {
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3 shadow-sm">
      {/* Product Image Skeleton */}
      <Skeleton className="h-14 w-14 flex-shrink-0 rounded-lg" />

      {/* Product Info Skeleton */}
      <div className="min-w-0 flex-grow space-y-2">
        {/* Product name and badge area */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-16" />
        </div>

        {/* Price */}
        <Skeleton className="h-4 w-20" />

        {/* Stock info area */}
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Skeleton className="h-2 w-2 rounded-full" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="w-18 h-3" />
          </div>
        </div>
      </div>

      {/* Quantity input and button area */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-10 w-16" />
        <Skeleton className="h-10 w-10" />
      </div>
    </div>
  )
}

// Interface for stock information - imported from parent
interface StockInfo {
  shouldShowStock: boolean
  stockReal: number
  stockReserved: number
  stockAvailable: number
  shouldShowProduct: boolean
}

// Enhanced interface for stock information considering visual updates
interface EnhancedStockInfo extends StockInfo {
  visualStockReal: number
  visualStockReserved: number
  visualStockAvailable: number
}

// Interface for batch information
interface BatchInfo {
  hasBatches: boolean
  batchCount: number
  singleBatchCode?: string
}

interface ManagerStockProps {
  products?: InvoiceFullProductData[]
  isLoadingProducts: boolean
  isErrorProducts: boolean
  isFetchingNextPage?: boolean
  canLoadMore?: boolean
  fetchNextPage?: () => Promise<unknown>
  refetchProducts?: () => void
  searchTerm: string
  onSearchChange: (_value: string) => void
  selectedCategory: string
  onCategoryChange: (_value: string) => void
  availableCategories?: SalesCategories[]
  isLoadingSalesCategories: boolean
  isErrorSalesCategories: boolean
  onAddProduct: (_product: InvoiceFullProductData, _quantity?: number) => void
  getAvailableStock: (_productId: number) => number
  focusedProductId?: number
  ensureProductLoaded?: (_productId: number) => Promise<boolean>
  getStockInfo: (_product: InvoiceFullProductData) => StockInfo // Original stock info
  getEnhancedStockInfo: (_product: InvoiceFullProductData) => EnhancedStockInfo // Enhanced stock info
  formatStockNumber: (_value: number, _measure?: string) => string // Formatting function
  isTemporaryChanges?: boolean // true for wizard mode, false for real database changes
  priceSystemId: number // Price system ID for correct price extraction
  documentType?: string // 'factura', 'prefactura', 'purchaseOrder', etc.
  className?: string // Add className prop for layout control
}

export const ManagerStock = ({
  products,
  isLoadingProducts,
  isErrorProducts,
  isFetchingNextPage,
  canLoadMore,
  fetchNextPage,
  refetchProducts,
  searchTerm,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  availableCategories,
  isLoadingSalesCategories,
  isErrorSalesCategories,
  onAddProduct,
  getAvailableStock,
  focusedProductId,
  ensureProductLoaded,
  getStockInfo,
  getEnhancedStockInfo,
  formatStockNumber,
  isTemporaryChanges = false,
  priceSystemId,
  documentType,
  className,
}: ManagerStockProps) => {
  // Ref for the products container
  const productsContainerRef = useRef<HTMLDivElement>(null)

  // Ref for scroll observer target (load more trigger)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)

  // Map to store product element refs
  const productRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  // State to track quantity input for each product
  const [productQuantities, setProductQuantities] = useState<
    Map<number, number>
  >(new Map())

  // State to track expanded batch products
  const [expandedBatchProducts, setExpandedBatchProducts] = useState<
    Set<number>
  >(new Set())

  // State to track wiggling inputs (for animation when action is blocked)
  const [wigglingInputs, setWigglingInputs] = useState<Set<number>>(new Set())

  // Helper function to determine if product has batches
  const getBatchInfo = useCallback(
    (product: InvoiceFullProductData): BatchInfo => {
      // Check if product has batches from the API data
      const hasBatches = !!(product.batches && product.batches.length > 0)
      const batchCount = hasBatches ? product.batches?.length : 0
      // If there's only one batch, get its code
      const singleBatchCode =
        hasBatches && batchCount === 1
          ? product.batches?.[0]?.uniqueCode
          : undefined
      return {
        hasBatches,
        batchCount: batchCount ?? 0,
        singleBatchCode,
      }
    },
    []
  )

  // Enhanced focus handling with auto-loading
  const handleFocusProduct = useCallback(
    async (productId: number) => {
      try {
        // First check if product is already visible
        const existingElement = productRefs.current.get(productId)

        if (existingElement && productsContainerRef.current) {
          existingElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          })
          return
        }

        // Check if product is in the products array but element not ref'd yet
        const productInList = products?.find((p) => p.id === productId)

        if (productInList && !existingElement) {
          // Wait a bit for React to render and try again
          await new Promise((resolve) => setTimeout(resolve, 200))
          const retryElement = productRefs.current.get(productId)
          if (retryElement) {
            retryElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            })
            return
          }
        }

        // If ensureProductLoaded is available, try to load the product
        if (ensureProductLoaded) {
          // Use a timeout to prevent indefinite waiting
          const loadPromise = ensureProductLoaded(productId)
          const timeoutPromise = new Promise<boolean>(
            (_, reject) =>
              setTimeout(() => reject(new Error('Load timeout')), 20000) // 20 second timeout
          )

          try {
            const isLoaded = await Promise.race([loadPromise, timeoutPromise])

            if (isLoaded) {
              // Wait a bit for React to render the new elements
              await new Promise((resolve) => setTimeout(resolve, 300))

              // Try to scroll to the element
              const focusedElement = productRefs.current.get(productId)

              if (focusedElement) {
                focusedElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest',
                })
              }
            }
          } catch {
            // console.warn('Product focus timeout or error:', error)
          }
        }
      } catch {
        //console.error('Error focusing product:', error)
      }
    },
    [products, ensureProductLoaded]
  )

  // Handle scrolling to focused product - make this non-blocking
  useEffect(() => {
    if (focusedProductId) {
      // Use setTimeout to make this non-blocking
      const timeoutId = setTimeout(() => {
        handleFocusProduct(focusedProductId)
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [focusedProductId, handleFocusProduct])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!canLoadMore || !fetchNextPage || !loadMoreTriggerRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting && canLoadMore && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        root: productsContainerRef.current,
        rootMargin: '100px', // Trigger 100px before reaching the bottom
        threshold: 0.1,
      }
    )

    const currentTarget = loadMoreTriggerRef.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [canLoadMore, fetchNextPage, isFetchingNextPage])

  // Function to set product ref
  const setProductRef = (productId: number, element: HTMLDivElement | null) => {
    if (element) {
      productRefs.current.set(productId, element)
    } else {
      productRefs.current.delete(productId)
    }
  }

  // Get quantity for a specific product (defaults to 1)
  const getProductQuantity = useCallback(
    (productId: number): number => {
      return productQuantities.get(productId) ?? 1
    },
    [productQuantities]
  )

  // Handle add product with quantity validation
  const handleAddProductWithQuantity = useCallback(
    (product: InvoiceFullProductData) => {
      const quantity = getProductQuantity(product.id)
      if (quantity > 0) {
        onAddProduct(product, quantity)
        // Reset quantity after adding
        setProductQuantities((prev) => {
          const newMap = new Map(prev)
          newMap.delete(product.id)
          return newMap
        })
      }
    },
    [getProductQuantity, onAddProduct]
  )

  // Handle quantity input change
  const handleQuantityChange = useCallback(
    (productId: number, value: string, maxDecimals: number) => {
      // Allow only numbers with controlled decimal precision
      let normalizedValue = value.replace(/[^0-9.]/g, '')
      // Only allow one dot
      const parts = normalizedValue.split('.')
      if (parts.length > 2) {
        normalizedValue = parts[0] + '.' + parts.slice(1).join('')
      }
      // Limit to the allowed number of decimal places
      if (normalizedValue.includes('.')) {
        const [intPart, decPart] = normalizedValue.split('.')
        normalizedValue =
          intPart + '.' + (decPart ? decPart.slice(0, maxDecimals) : '')
      }
      // Parse as float, fallback to 0 if invalid
      const numValue = parseFloat(normalizedValue)
      setProductQuantities((prev) =>
        new Map(prev).set(productId, isNaN(numValue) ? 0 : numValue)
      )
    },
    []
  )

  // Reset quantities when products change (search, category, area)
  useEffect(() => {
    setProductQuantities(new Map())
  }, [searchTerm, selectedCategory])

  // Auto-load more products when current page has no visible products due to filtering
  useEffect(() => {
    // Only auto-load if:
    // 1. We're not currently loading
    // 2. We can load more pages
    // 3. We have raw products but no filtered products (everything was filtered out)
    // 4. We're not in an error state
    if (
      !isLoadingProducts &&
      !isFetchingNextPage &&
      canLoadMore &&
      fetchNextPage &&
      products &&
      products.length === 0 &&
      !isErrorProducts
    ) {
      // Debounce the auto-load to prevent rapid consecutive calls
      const timeoutId = setTimeout(() => {
        fetchNextPage()
      }, 100) // Small delay to allow state to settle

      return () => clearTimeout(timeoutId)
    }
  }, [
    products,
    isLoadingProducts,
    isFetchingNextPage,
    canLoadMore,
    fetchNextPage,
    isErrorProducts,
  ])

  // Handle expanding/collapsing batch products
  const handleToggleBatchExpansion = useCallback((productId: number) => {
    setExpandedBatchProducts((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(productId)) {
        newSet.delete(productId)
      } else {
        newSet.add(productId)
      }
      return newSet
    })
  }, [])

  // Handle wiggle animation when action is blocked
  const triggerWiggle = useCallback((productId: number) => {
    setWigglingInputs((prev) => new Set(prev).add(productId))
    // Remove wiggle after animation completes
    setTimeout(() => {
      setWigglingInputs((prev) => {
        const newSet = new Set(prev)
        newSet.delete(productId)
        return newSet
      })
    }, 500) // Duration should match CSS animation
  }, [])

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Search and Filter Section */}
      <div className="mt-11 flex flex-shrink-0 items-center justify-between gap-2 border-b bg-background pb-4">
        {/* Search Input */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-10"
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar productos..."
            value={searchTerm}
          />
        </div>

        {/* Category Filter */}
        <Select
          disabled={isLoadingSalesCategories || isErrorSalesCategories}
          onValueChange={onCategoryChange}
          value={selectedCategory}
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="Todas las categorías" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem className="cursor-pointer" value="all">
              Todas las categorías
            </SelectItem>
            {availableCategories?.map((category) => (
              <SelectItem
                className="cursor-pointer"
                key={category.id}
                value={category.id.toString()}
              >
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Section */}
      <div className="flex-grow overflow-hidden">
        {isErrorProducts ? (
          <div
            className={cn(
              'flex h-full flex-col items-center justify-center',
              'rounded-md border border-dashed p-8 text-center'
            )}
          >
            <PackageOpen className="h-8 w-8 text-muted-foreground" />
            <h3 className="mt-4 text-sm font-medium">
              Error al cargar productos
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              No se pudieron cargar los productos. Intenta de nuevo.
            </p>
            {refetchProducts && (
              <Button
                className="mt-4"
                onClick={refetchProducts}
                size="sm"
                variant="outline"
              >
                Reintentar
              </Button>
            )}
          </div>
        ) : (
          <div
            className="grid h-[calc(100vh-22rem)] w-full gap-3 overflow-y-auto pr-2 pt-2"
            ref={productsContainerRef}
          >
            {isLoadingProducts ? (
              // Show skeleton cards while loading
              <>
                {Array(6)
                  .fill(0)
                  .map((_, index) => (
                    <ProductCardSkeleton key={`skeleton-${index}`} />
                  ))}
              </>
            ) : products && products?.length === 0 ? (
              // Only show "no products" when we've finished loading and there are truly no products
              // If we can still load more, we're in auto-loading mode
              canLoadMore ? (
                // Still loading more pages automatically
                <div
                  className={cn(
                    'flex h-full flex-col items-center justify-center',
                    'rounded-md border border-dashed p-8 text-center'
                  )}
                >
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <h3 className="mt-4 text-sm font-medium">
                    Buscando productos disponibles...
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Aplicando filtros de disponibilidad de stock.
                  </p>
                </div>
              ) : (
                // No more pages to load and no products found
                <div
                  className={cn(
                    'flex h-full flex-col items-center justify-center',
                    'rounded-md border border-dashed p-8 text-center'
                  )}
                >
                  <PackageOpen className="h-8 w-8 text-muted-foreground" />
                  <h3 className="mt-4 text-sm font-medium">
                    No se encontraron productos disponibles
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Los productos pueden estar fuera de stock o no cumplir los
                    criterios de disponibilidad.
                    <br />
                    Intenta cambiar tu búsqueda o filtros.
                  </p>
                </div>
              )
            ) : (
              <>
                {products?.map((product) => {
                  const stockInfo = getStockInfo(product)
                  const enhancedStockInfo = getEnhancedStockInfo(product)
                  const batchInfo = getBatchInfo(product)
                  const isOutOfStock =
                    stockInfo.shouldShowStock &&
                    enhancedStockInfo.visualStockAvailable <= 0
                  const mainPrice = getProductMainPrice(product, priceSystemId)
                  const mainCurrency = getProductMainCurrency(
                    product,
                    priceSystemId
                  )
                  const isFocused = focusedProductId === product.id
                  const isExpanded = expandedBatchProducts.has(product.id)

                  // Get current quantity input value
                  const currentQuantity = getProductQuantity(product.id)
                  const availableStock = getAvailableStock(product.id)
                  const hasStockLimit = Boolean(
                    product.stockLimit &&
                      stockInfo.shouldShowStock &&
                      product.type !== 'SERVICE' &&
                      product.type !== 'ADDON'
                  )

                  // Check if current quantity would exceed available stock
                  const wouldExceedStock =
                    hasStockLimit && currentQuantity > availableStock
                  const maxAllowedQuantity = hasStockLimit
                    ? availableStock
                    : 999

                  // Determine if this product should show quantity input and batch info
                  const showQuantityInput =
                    documentType !== 'purchaseOrder' || !batchInfo.hasBatches
                  const showBatchInfo =
                    documentType === 'purchaseOrder' && batchInfo.hasBatches
                  const showSingleBatchCode =
                    documentType === 'purchaseOrder' &&
                    batchInfo.singleBatchCode
                  const quantityDecimals = getQuantityDecimalsForProduct(
                    product,
                    currentQuantity
                  )

                  return (
                    <div
                      className={cn(
                        'flex items-start gap-3 rounded-lg border p-3 shadow-sm transition-all duration-300',
                        isOutOfStock
                          ? 'bg-muted/30 opacity-70'
                          : isFocused
                            ? 'border-blue-500 bg-blue-50/50 shadow-md ring-2 ring-blue-200/50'
                            : 'hover:bg-muted/50'
                      )}
                      key={product.id}
                      ref={(el) => setProductRef(product.id, el)}
                    >
                      {/* Product Image */}
                      <ImageComponent
                        className={cn(
                          'h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg border transition-all',
                          isFocused && 'border-blue-400 shadow-sm'
                        )}
                        hash={product.images?.[0]?.blurHash}
                        src={product.images?.[0]?.src}
                      />

                      {/* Product Info and Controls */}

                      <div className="flex min-w-0 flex-1 flex-col gap-2">
                        {/* Product Header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger className="max-w-[180px] truncate">
                                    <span
                                      className={cn(
                                        'text-sm font-medium',
                                        isFocused && 'text-blue-900'
                                      )}
                                    >
                                      {product.name}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{product.name}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>

                            {/* Price and Currency */}
                            <div
                              className={cn(
                                'text-sm font-semibold',
                                isFocused ? 'text-blue-700' : 'text-primary'
                              )}
                            >
                              {mainPrice.toFixed(2)} {mainCurrency}
                            </div>
                          </div>

                          {product.type === 'SERVICE' && (
                            <Badge
                              className="flex-shrink-0 text-xs"
                              variant="secondary"
                            >
                              Servicio
                            </Badge>
                          )}

                          {/* Quantity Input and Add Button */}
                          <div className="flex flex-shrink-0 items-center gap-2">
                            {showQuantityInput && (
                              <Input
                                className={cn(
                                  'h-10 w-16 text-center text-sm transition-all',
                                  wouldExceedStock &&
                                    'border-red-500 bg-red-50',
                                  wigglingInputs.has(product.id) &&
                                    'animate-wiggle'
                                )}
                                inputMode="decimal"
                                max={
                                  hasStockLimit ? maxAllowedQuantity : undefined
                                }
                                min={0}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    product.id,
                                    e.target.value,
                                    quantityDecimals
                                  )
                                }
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()

                                    // Check the same conditions as the button's disabled state
                                    const isDisabled =
                                      isOutOfStock ||
                                      (!batchInfo.hasBatches &&
                                        wouldExceedStock) ||
                                      (!batchInfo.hasBatches &&
                                        currentQuantity <= 0)

                                    if (isDisabled) {
                                      // Trigger wiggle animation to show action is blocked
                                      triggerWiggle(product.id)
                                      return
                                    }

                                    // Proceed with add action
                                    if (batchInfo.hasBatches) {
                                      onAddProduct(product, 1) // For batch products, always add 1
                                    } else {
                                      handleAddProductWithQuantity(product)
                                    }
                                  }
                                }}
                                step={quantityDecimals === 3 ? 0.001 : 0.01}
                                title={
                                  hasStockLimit
                                    ? `Máximo disponible: ${maxAllowedQuantity}`
                                    : undefined
                                }
                                type="number"
                                value={currentQuantity}
                              />
                            )}
                            <Button
                              aria-label={`Añadir ${batchInfo.hasBatches ? 1 : currentQuantity} ${product.name}`}
                              className={cn(
                                'h-10 w-10 flex-shrink-0 transition-all',
                                isFocused &&
                                  'bg-blue-600 text-white shadow-md hover:bg-blue-700',
                                wouldExceedStock &&
                                  'cursor-not-allowed opacity-50'
                              )}
                              disabled={
                                isOutOfStock ||
                                (!batchInfo.hasBatches && wouldExceedStock) ||
                                (!batchInfo.hasBatches && currentQuantity <= 0)
                              }
                              onClick={() =>
                                batchInfo.hasBatches
                                  ? onAddProduct(product, 1) // For batch products, always add 1
                                  : handleAddProductWithQuantity(product)
                              }
                              size="icon"
                              variant={isFocused ? 'default' : 'outline'}
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Stock and Details */}
                        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                          {showBatchInfo && (
                            <div className="flex flex-col gap-1">
                              {showSingleBatchCode ? (
                                // Single batch - show batch code directly
                                <div className="flex items-center gap-1">
                                  <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                                  <span className="text-xs text-blue-600">
                                    Lote: {batchInfo.singleBatchCode}
                                  </span>
                                </div>
                              ) : (
                                // Multiple batches - show expand/collapse
                                <div className="flex items-center gap-1">
                                  <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                                  <span className="text-xs text-blue-600">
                                    {batchInfo.batchCount} lotes disponibles
                                  </span>
                                  <Button
                                    className="h-4 w-4 p-0"
                                    onClick={() =>
                                      handleToggleBatchExpansion(product.id)
                                    }
                                    size="sm"
                                    variant="ghost"
                                  >
                                    <ChevronDown
                                      className={cn(
                                        'h-3 w-3 transition-transform',
                                        isExpanded && 'rotate-180'
                                      )}
                                    />
                                  </Button>
                                </div>
                              )}

                              {/* Expanded batch list */}
                              {isExpanded && !batchInfo.singleBatchCode && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {product.batches?.map((batch, index) => (
                                    <div
                                      className="flex items-center gap-1 text-xs"
                                      key={batch.id}
                                    >
                                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400"></span>
                                      <span>
                                        Lote {index + 1}: {batch.uniqueCode}
                                        {batch.expirationAt && (
                                          <span className="text-gray-500">
                                            {' '}
                                            (Exp:{' '}
                                            {new Date(
                                              batch.expirationAt
                                            ).toLocaleDateString()}
                                            )
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Enhanced Stock Information - Based on Business Rules and Visual Updates */}
                          {product.type !== 'SERVICE' &&
                            enhancedStockInfo.shouldShowStock && (
                              <div className="flex flex-col gap-1">
                                {enhancedStockInfo.visualStockReserved > 0 ? (
                                  // Show detailed breakdown when there's reserved stock
                                  <>
                                    <div className="flex items-center gap-1">
                                      <span
                                        className={cn(
                                          'h-2 w-2 rounded-full',
                                          enhancedStockInfo.visualStockAvailable >
                                            10
                                            ? 'bg-green-500'
                                            : enhancedStockInfo.visualStockAvailable >
                                                0
                                              ? 'bg-yellow-500'
                                              : 'bg-red-500'
                                        )}
                                        title={`Stock Real: ${formatStockNumber(
                                          enhancedStockInfo.visualStockReal,
                                          product.measure
                                        )} | Reservado: ${formatStockNumber(
                                          enhancedStockInfo.visualStockReserved,
                                          product.measure
                                        )} | Disponible: ${formatStockNumber(
                                          enhancedStockInfo.visualStockAvailable,
                                          product.measure
                                        )}`}
                                      ></span>
                                      <span
                                        className={cn(
                                          'text-xs',
                                          // Highlight if real stock has been visually modified
                                          enhancedStockInfo.visualStockReal !==
                                            enhancedStockInfo.stockReal &&
                                            'font-semibold text-blue-700'
                                        )}
                                      >
                                        Real:{' '}
                                        {formatStockNumber(
                                          enhancedStockInfo.visualStockReal,
                                          product.measure
                                        )}
                                        {enhancedStockInfo.visualStockReal !==
                                          enhancedStockInfo.stockReal && (
                                          <span className="ml-1 text-xs text-blue-600">
                                            (
                                            {isTemporaryChanges
                                              ? 'original'
                                              : 'era'}{' '}
                                            {formatStockNumber(
                                              enhancedStockInfo.stockReal,
                                              product.measure
                                            )}
                                            )
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    <div className="text-xs">
                                      <span
                                        className={cn(
                                          enhancedStockInfo.visualStockReserved !==
                                            enhancedStockInfo.stockReserved
                                            ? 'font-semibold text-blue-700'
                                            : 'text-orange-600'
                                        )}
                                      >
                                        Reservado:{' '}
                                        {formatStockNumber(
                                          enhancedStockInfo.visualStockReserved,
                                          product.measure
                                        )}
                                        {enhancedStockInfo.visualStockReserved !==
                                          enhancedStockInfo.stockReserved && (
                                          <span className="ml-1 text-xs text-blue-600">
                                            (
                                            {isTemporaryChanges
                                              ? 'original'
                                              : 'era'}{' '}
                                            {formatStockNumber(
                                              enhancedStockInfo.stockReserved,
                                              product.measure
                                            )}
                                            )
                                          </span>
                                        )}
                                      </span>
                                      {' | '}
                                      <span
                                        className={cn(
                                          enhancedStockInfo.visualStockAvailable !==
                                            enhancedStockInfo.stockAvailable
                                            ? 'font-semibold text-blue-700'
                                            : 'text-green-600'
                                        )}
                                      >
                                        Disponible:{' '}
                                        {formatStockNumber(
                                          enhancedStockInfo.visualStockAvailable,
                                          product.measure
                                        )}
                                        {enhancedStockInfo.visualStockAvailable !==
                                          enhancedStockInfo.stockAvailable && (
                                          <span className="ml-1 text-xs text-blue-600">
                                            (
                                            {isTemporaryChanges
                                              ? 'original'
                                              : 'era'}{' '}
                                            {formatStockNumber(
                                              enhancedStockInfo.stockAvailable,
                                              product.measure
                                            )}
                                            )
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  // Show only available when no reserved stock
                                  <div className="flex items-center gap-1">
                                    <span
                                      className="h-2 w-2 rounded-full bg-green-500"
                                      title={`Disponible: ${formatStockNumber(
                                        enhancedStockInfo.visualStockAvailable,
                                        product.measure
                                      )}`}
                                    ></span>
                                    <span
                                      className={cn(
                                        'text-xs',
                                        enhancedStockInfo.visualStockAvailable !==
                                          enhancedStockInfo.stockAvailable
                                          ? 'font-semibold text-blue-700'
                                          : 'text-green-600'
                                      )}
                                    >
                                      Disponible:{' '}
                                      {formatStockNumber(
                                        enhancedStockInfo.visualStockAvailable,
                                        product.measure
                                      )}
                                      {enhancedStockInfo.visualStockAvailable !==
                                        enhancedStockInfo.stockAvailable && (
                                        <span className="ml-1 text-xs text-blue-600">
                                          (
                                          {isTemporaryChanges
                                            ? 'original'
                                            : 'era'}{' '}
                                          {formatStockNumber(
                                            enhancedStockInfo.stockAvailable,
                                            product.measure
                                          )}
                                          )
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Show unlimited stock indicator when stock is not tracked */}
                          {product.type !== 'SERVICE' &&
                            !enhancedStockInfo.shouldShowStock && (
                              <span className="flex items-center gap-1">
                                <span
                                  className="h-2 w-2 rounded-full bg-blue-500"
                                  title="Stock ilimitado"
                                ></span>
                                Stock: ∞
                              </span>
                            )}
                        </div>

                        {/* Product details (weight, cubaje) */}
                        <div className="flex flex-wrap gap-2">
                          {product.netWeight && (
                            <span className="flex items-center gap-1">
                              <Weight className="h-3 w-3" />
                              {product.netWeight} kg
                            </span>
                          )}

                          {product.cubaje && (
                            <span className="flex items-center gap-1">
                              <Box className="h-3 w-3" />
                              {product.cubaje} m³
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* Infinite scroll trigger and loading indicator */}
                {canLoadMore && (
                  <div className="py-2" ref={loadMoreTriggerRef}>
                    {/* This div acts as the intersection observer target for auto-loading */}
                  </div>
                )}

                {/* Show loading indicator when fetching more products */}
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Cargando más productos...</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
